import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';

const PublicProfile = () => {
  const { userId } = useParams();
  const { isDarkMode } = useTheme();
  const { user, userProfile, handleSupportCreator } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topSupporters, setTopSupporters] = useState([]);
  const [supportersLoading, setSupportersLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        const profileData = {
          id: userId,
          displayName: userData.displayName || 'Anonymous',
          photoURL: userData.photoURL || null,
          accountType: userData.accountType || 'player',
          isCreator: userData.isCreator || false,
          platform: userData.creatorProfile?.platform || userData.platform || null,
          contentType: userData.creatorProfile?.contentType || null,
          channelUrl: userData.channelUrl || userData.promotionalURL || userData.creatorProfile?.channelUrl || null,
          bio: userData.creatorProfile?.bio || '',
          bannerURL: userData.bannerURL || null,
          totalGamesPlayed: userData.totalGamesPlayed || 0,
          totalPointsEarned: userData.totalPointsEarned || 0,
          thankYouMessage: userData.thankYouMessage || '',
          uniqueSupporters: userData.uniqueSupporters || 0
        };
        setProfile(profileData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  // Fetch top 10 supporters for creators
  useEffect(() => {
    const fetchTopSupporters = async () => {
      if (!profile || (profile.accountType !== 'creator' && !profile.isCreator)) {
        return;
      }
      
      try {
        setSupportersLoading(true);
        
        // Query supporters subcollection, ordered by totalPoints
        const supportersRef = collection(db, 'users', userId, 'supporters');
        const supportersQuery = query(supportersRef, orderBy('totalPoints', 'desc'), limit(10));
        const supportersSnapshot = await getDocs(supportersQuery);
        
        // Fetch user data for each supporter
        const supportersList = [];
        for (const supporterDoc of supportersSnapshot.docs) {
          const supporterData = supporterDoc.data();
          
          // Fetch supporter's user profile
          const supporterUserRef = doc(db, 'users', supporterDoc.id);
          const supporterUserDoc = await getDoc(supporterUserRef);
          
          if (supporterUserDoc.exists()) {
            const supporterUserData = supporterUserDoc.data();
            supportersList.push({
              id: supporterDoc.id,
              displayName: supporterUserData.displayName || 'Anonymous',
              photoURL: supporterUserData.photoURL || null,
              totalPoints: supporterData.totalPoints || 0,
              gamesPlayed: supporterData.gamesPlayed || 0
            });
          }
        }
        
        setTopSupporters(supportersList);
        setSupportersLoading(false);
      } catch (err) {
        console.error('Error fetching supporters:', err);
        setSupportersLoading(false);
      }
    };
    
    fetchTopSupporters();
  }, [profile, userId]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className={`text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Loading profile...
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className={`text-2xl font-bold mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {error || 'Profile not found'}
          </h2>
          <Link 
            to="/"
            className="mt-4 inline-block px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const isCreator = profile.accountType === 'creator' || profile.isCreator === true;
  const isOwnProfile = user?.uid === profile.id;
  const isSupporting = userProfile?.supportedCreatorId === profile.id;

  const getPlatformIcon = (platform, size = 'w-5 h-5') => {
    const icons = {
      'YouTube': '/images/youtube-icon.png',
      'Instagram': '/images/instagram-icon.png',
      'Twitch': '/images/twitch-icon.png',
      'TikTok': '/images/tiktok-icon.png'
    };
    
    const iconUrl = icons[platform];
    
    if (!iconUrl) {
      return null;
    }
    
    return <img src={iconUrl} alt={`${platform} icon`} className={`${size} object-contain`} />;
  };

  const getPlatformDisplay = (platform) => {
    // Show platform icon with name
    return (
      <span className="flex items-center gap-2">
        {getPlatformIcon(platform)}
        <span>{platform}</span>
      </span>
    );
  };

  const getPlatformBorderColor = (platform) => {
    if (!platform) return isDarkMode ? 'border-white/20' : 'border-gray-200';
    
    const colors = {
      'YouTube': 'border-red-500',
      'Twitch': 'border-purple-500',
      'TikTok': 'border-black',
      'Instagram': 'border-pink-600'
    };
    return colors[platform] || (isDarkMode ? 'border-white/20' : 'border-gray-200');
  };

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Back button */}
        <Link 
          to="/"
          className={`inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-colors ${
            isDarkMode 
              ? 'bg-white/10 hover:bg-white/20 text-white' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
          }`}
        >
          ← Back to App
        </Link>

        {/* Profile Card */}
        <div className={`rounded-2xl shadow-lg overflow-hidden border-4 ${
          isCreator ? getPlatformBorderColor(profile.platform) : (isDarkMode ? 'border-white/20' : 'border-gray-200')
        } ${
          isDarkMode ? 'bg-white/10' : 'bg-white'
        }`}>
          {/* Banner Image */}
          {isCreator && profile.bannerURL && (
            <div className="w-full h-48 md:h-64 overflow-hidden">
              <img 
                src={profile.bannerURL}
                alt="Profile Banner"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-8">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Profile Picture */}
            {profile.photoURL ? (
              <img 
                src={profile.photoURL}
                alt={profile.displayName}
                className="w-32 h-32 rounded-full border-4 border-purple-500 object-cover"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-5xl border-4 border-purple-500">
                {profile.displayName.charAt(0).toUpperCase()}
              </div>
            )}

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {profile.displayName}
              </h1>

              {/* Badges Row */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                {/* Account Type Badge */}
                {isCreator ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold text-sm">
                    ⭐ Creator
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                    isDarkMode 
                      ? 'bg-blue-500/20 text-blue-300' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    🎮 Player
                  </span>
                )}

                {/* Platform Badge */}
                {isCreator && profile.platform && (
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                    isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {getPlatformDisplay(profile.platform)}
                  </span>
                )}

                {/* Content Type Badge */}
                {isCreator && profile.contentType && (
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                    isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {profile.contentType}
                  </span>
                )}

                {/* Supporters Count */}
                {isCreator && profile.uniqueSupporters > 0 && (
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
                    isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'
                  }`}>
                    💚 {profile.uniqueSupporters} {profile.uniqueSupporters === 1 ? 'Supporter' : 'Supporters'}
                  </span>
                )}
              </div>

              {/* Bio (if creator) */}
              {isCreator && profile.bio && (
                <p className={`text-sm md:text-base mb-4 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {profile.bio}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                {/* Support Button */}
                {isCreator && !isOwnProfile && user && (
                  <button
                    onClick={() => handleSupportCreator(profile.id)}
                    disabled={isSupporting}
                    className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                      isSupporting
                        ? isDarkMode 
                          ? 'bg-green-500/20 text-green-300 cursor-default'
                          : 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                    }`}
                  >
                    {isSupporting ? '✓ Currently Supporting' : 'Support This Creator'}
                  </button>
                )}

                {/* Channel Link - Always shown for creators */}
                {isCreator && (
                  <a
                    href={profile.channelUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-semibold"
                  >
                    Visit Channel
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className={`mt-8 pt-8 border-t ${
            isDarkMode ? 'border-white/20' : 'border-gray-200'
          }`}>
            <h2 className={`text-xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Stats
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-white/5' : 'bg-gray-50'
              }`}>
                <div className={`text-3xl font-bold ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                }`}>
                  {profile.totalPointsEarned.toLocaleString()}
                </div>
                <div className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Total Points {isCreator ? 'Received' : 'Earned'}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-white/5' : 'bg-gray-50'
              }`}>
                <div className={`text-3xl font-bold ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {profile.totalGamesPlayed.toLocaleString()}
                </div>
                <div className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Games Played
                </div>
              </div>
            </div>
          </div>

          {/* Top Supporters (Phase 3) */}
          {isCreator && (
            <div className={`mt-8 pt-8 border-t ${
              isDarkMode ? 'border-white/20' : 'border-gray-200'
            }`}>
              {/* Thank You Message (Phase 5) */}
              {profile.thankYouMessage && (
                <div className={`mb-6 p-4 rounded-lg border-2 ${
                  isDarkMode 
                    ? 'bg-purple-500/10 border-purple-500/30' 
                    : 'bg-purple-50 border-purple-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💜</div>
                    <div className="flex-1">
                      <div className={`text-sm font-semibold mb-1 ${
                        isDarkMode ? 'text-purple-300' : 'text-purple-600'
                      }`}>
                        Message from {profile.displayName}
                      </div>
                      <p className={`${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {profile.thankYouMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <h2 className={`text-xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Top Supporters
              </h2>
              
              {supportersLoading ? (
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Loading supporters...
                </p>
              ) : topSupporters.length === 0 ? (
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  No supporters yet. Be the first to support this creator!
                </p>
              ) : (
                <div className="space-y-3">
                  {topSupporters.map((supporter, index) => (
                    <Link
                      key={supporter.id}
                      to={`/profile/${supporter.id}`}
                      className={`block p-4 rounded-lg transition-all hover:scale-[1.02] ${
                        isDarkMode 
                          ? 'bg-white/5 hover:bg-white/10' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                          index === 0 
                            ? 'bg-yellow-400 text-yellow-900'
                            : index === 1
                            ? 'bg-gray-300 text-gray-700'
                            : index === 2
                            ? 'bg-orange-400 text-orange-900'
                            : isDarkMode
                            ? 'bg-white/10 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}>
                          {index === 0 ? '👑' : `#${index + 1}`}
                        </div>
                        
                        {/* Profile Picture */}
                        {supporter.photoURL ? (
                          <img 
                            src={supporter.photoURL}
                            alt={supporter.displayName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xl font-bold text-white">
                            {supporter.displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        
                        {/* Supporter Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold truncate ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {supporter.displayName}
                          </h3>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {supporter.gamesPlayed} games played
                          </p>
                        </div>
                        
                        {/* Points */}
                        <div className="text-right">
                          <div className={`text-xl font-bold ${
                            isDarkMode ? 'text-purple-400' : 'text-purple-600'
                          }`}>
                            {supporter.totalPoints.toLocaleString()}
                          </div>
                          <div className={`text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            points
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
